import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axiosInstance from "../../api/axiosInstance";
import { isAuthenticated } from "../../utils/auth";

const initialState = {
  sports: [],
  matchResults: [],
  activeRequestsCount: 0,
  loading: false,
  initialLoad: true,
  error: null,
};

export const fetchSportsAsync = createAsyncThunk(
  "sport/fetchSports",
  async (_, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.post("/sport/sports");
      return response.data || response;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch sports",
      );
    }
  },
);

export const fetchSearchMatchesAsync = createAsyncThunk(
  "sport/fetchSearchMatches",
  async (_, { rejectWithValue }) => {
    const isLoggedIn = isAuthenticated();
    try {
      const response = await axiosInstance.post(
        isLoggedIn ? "/match/getMatches" : "/match/matches",
        {},
      );
      return response.data || response;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to search matches",
      );
    }
  },
);

export const fetchMatchResultsBySportAsync = createAsyncThunk(
  "sport/fetchMatchResultsBySport",
  async (payload, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.post("/match/matchResults", payload);
      return response.data || response;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch match results",
      );
    }
  },
);

const sportSlice = createSlice({
  name: "sport",
  initialState,
  reducers: {
    clearSports: (state) => {
      state.sports = [];
      state.error = null;
      state.initialLoad = true;
    },
    resetMatchResults: (state) => {
      state.matchResults = [];
      state.activeRequestsCount = 0;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchSportsAsync.pending, (state) => {
        if (state.initialLoad) {
          state.loading = true;
        }
        state.error = null;
      })
      .addCase(fetchSportsAsync.fulfilled, (state, action) => {
        state.loading = false;
        state.initialLoad = false;
        state.sports = action.payload;
        state.error = null;
      })
      .addCase(fetchSportsAsync.rejected, (state, action) => {
        state.loading = false;
        state.initialLoad = false;
        state.error = action.payload;
      })
      .addCase(fetchMatchResultsBySportAsync.pending, (state) => {
        state.activeRequestsCount += 1;
      })
      .addCase(fetchMatchResultsBySportAsync.fulfilled, (state, action) => {
        state.activeRequestsCount -= 1;
        if (Array.isArray(action.payload)) {
          state.matchResults = [
            ...state.matchResults,
            ...(action.payload || []),
          ];
        }
      })
      .addCase(fetchMatchResultsBySportAsync.rejected, (state) => {
        state.activeRequestsCount -= 1;
      });
  },
});

export const { clearSports, resetMatchResults } = sportSlice.actions;
export default sportSlice.reducer;
